import { Avatar, Text } from "@radix-ui/themes";
import { IoMicOutline, IoVideocamOffOutline, IoVideocamOutline } from "react-icons/io5";
import { IoMicOffOutline } from "react-icons/io5";
import React, { useEffect, useRef, useState } from "react";
import { CiMenuKebab, CiVideoOn } from "react-icons/ci";
import { HiOutlineVideoCamera } from "react-icons/hi2";
import { HiOutlineVideoCameraSlash } from "react-icons/hi2";
import { Popover, Box } from "@radix-ui/themes";
import { SlBadge } from "react-icons/sl";
import { MdOutlineSwapHorizontalCircle } from "react-icons/md";
import { HiOutlineUserGroup } from "react-icons/hi";
import { Flex, Button, Dialog } from "@radix-ui/themes";
// import BadgesExchanged from "@/components/badges/BadgesExchanged";
import BadgeControl from "../controls/Badge";
import { useSelector } from "react-redux";
import { RootState } from "store";
import '../../../../styles/custom_avatar.css';
import useMediaQuery from "hooks/useMediaQuery";

interface ParticipantProps {
    name: string;
    size: any;
    streamArray?: any;
    id?: number;
    radius: any;
    caseStudyOn?: boolean;
    profilePhoto?: any;
    kebab?: boolean;
    audio?: string | number;
    video?: string | number;
    width?: string | number;
    height?: string | number;
    aspectRatio?: string | number;
    style: any;
}

const ParticipantExpanded: React.FC<ParticipantProps> = ({
    name,
    size,
    radius,
    streamArray,
    id,
    caseStudyOn = false,
    profilePhoto,
    kebab,
    audio,
    video,
    width = 192, // Default width in pixels
    height = 130,
    aspectRatio,
    style
}) => {
    // const [micOn, setMicOn] = useState(false);
    // const [videoOn, setVideoOn] = useState(false);
    const auth = useSelector((state: RootState) => state.auth.user);
    const videoRef = useRef<HTMLVideoElement>(null);
    const audioRef = useRef<HTMLAudioElement>(null);
    const isLocalParticipant = auth && name === `${auth.firstName} ${auth.lastName}`;
    const isMicOn = streamArray?.audio && !(streamArray.audio.remotelyPaused ?? false) && !(streamArray.audio.locallyPaused ?? false);
    const isVideoOn = streamArray?.video && !(streamArray.video.remotelyPaused ?? false) && !(streamArray.video.locallyPaused ?? false) && streamArray.video.track?.readyState === "live";
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);

    const startConsumer = async (consumer: any, ref: React.RefObject<HTMLVideoElement | HTMLAudioElement>) => {
        if (!consumer || !consumer.track || !(consumer.track instanceof MediaStreamTrack)) {
            console.warn(`Invalid consumer for ${consumer?.kind}:`, consumer);
            return;
        }

        try {
            const stream = new MediaStream([consumer.track]);
            if (ref.current) {
                ref.current.srcObject = stream;
                // Mute audio for local participant to prevent feedback
                // if (consumer.kind === "audio" && isLocalParticipant) {
                //     ref.current.muted = true;
                // }
                // await ref.current.play().then(() => console.log('Video play success'))
                //     .catch(err => {
                //         console.error('Video play error:', err);
                //         if (err.name === 'AbortError') {
                //             console.log('Ignoring AbortError on play');
                //         }
                //     });;
            }
        } catch (error) {
            console.error(`Error creating MediaStream for ${consumer.kind}:`, error);
        }
    };

    useEffect(() => {
        if (streamArray?.video && streamArray.video.track?.readyState === "live") {
            startConsumer(streamArray.video, videoRef);
        }
        if (streamArray?.audio && streamArray.audio.track?.readyState === "live" && !isLocalParticipant) {
            startConsumer(streamArray.audio, audioRef);
        }
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.srcObject = null;
            }
            if (videoRef.current) {
                videoRef.current.pause();
                videoRef.current.srcObject = null;
            }
        };
    }, [streamArray?.video?.track, streamArray?.audio?.track, isLocalParticipant]);

    const getDimension = (dim: number | string | undefined, isWidth: boolean = false): string => {
        if (dim === undefined) return isWidth ? `${width}px` : `${height}px`;
        if (typeof dim === 'number') return `${dim}px`;
        return dim;
    };

    const containerWidth = getDimension(width, true);
    const containerHeight = getDimension(height);
    const getTailwindSize = (size: number | string, isWidth: boolean): string => {
        if (typeof size === 'string') return size; // Assume it's a Tailwind class
        const sizeMap: { [key: number]: string } = isWidth
            ? {
                192: 'w-48',
                128: 'w-32',
                256: 'w-64',
                320: 'w-80',
                384: 'w-96',
            }
            : {
                144: 'h-36',
                96: 'h-24',
                192: 'h-48',
                240: 'h-60',
                288: 'h-72',
            };
        return sizeMap[size] || (isWidth ? 'w-48' : 'h-36');
    };

    const containerClasses = [
        'relative flex justify-center items-center',
        getTailwindSize(width, true),
        getTailwindSize(height, false),
        'mobile:w-28 mobile:h-20',
        'tablet:w-40 tablet:h-24',
        'laptop:w-48 laptop:h-28',
        'desktop:w-48 desktop:h-28',
        'largescreen:w-48 largescreen:h-28',
        'relative w-full h-[130px] rounded-lg overflow-hidden',
    ].join(' ');
    return (
        <div className="w-full" style={{ aspectRatio: aspectRatio, maxHeight: style?.maxHeight }}>
            <div className="relative w-full h-full bg-gray-700 rounded-xl overflow-hidden">
                {isVideoOn && streamArray?.video?.track?.readyState === "live" ? (
                    <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted={isLocalParticipant}
                        className="w-full h-full object-cover"
                    />
                ) : (
                    <div className="w-full h-full" style={{ borderRadius: '8px', background: '#5B5D6B' }}>
                        <Avatar
                            src={profilePhoto}
                            fallback={
                                name
                                    ? name
                                        .split(" ")
                                        .slice(0, 2)
                                        .map((n) => n.charAt(0).toUpperCase())
                                        .join("")
                                    : "T"
                            } size="7"
                            radius={radius}
                            className="full-width-avatar"
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                borderRadius: '8px',
                                background: 'radial-gradient(50% 86.76% at 50% 50%, rgba(33, 32, 37, 0.00) 0%, rgba(33, 32, 37, 0.30) 50%, rgba(33, 32, 37, 0.60) 100%)',
                                backgroundBlendMode: 'multiply',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#FFF',
                                fontSize: '20px',
                                fontWeight: '700',
                            }}
                        />
                    </div>
                )}
                {audio === "yes" && (
                    <div className="absolute left-1 top-1/2 -translate-y-[65%] p-1 rounded-full text-white mobile:text-xs tablet:text-base laptop:text-lg desktop:text-xl">
                        {isMicOn ? <IoMicOutline /> : <IoMicOffOutline />}
                    </div>
                )}

                {video === "yes" && (
                    <div className="absolute bottom-1 left-1 p-1 rounded-full text-white mobile:text-xs tablet:text-base laptop:text-lg desktop:text-xl">
                        {isVideoOn ? <IoVideocamOutline /> : <IoVideocamOffOutline />}
                    </div>
                )}
                <div className="absolute bottom-1 mobile:text-s right-1 px-2 py-0.5 rounded text-white text-xs truncate max-w-[70%]">
                    {name}
                </div>
                {/* {streamArray?.audio?.track?.readyState === "live" && !isLocalParticipant && (
                    <audio
                        ref={audioRef}
                        autoPlay
                        playsInline
                        muted={isLocalParticipant}
                        className="hidden"
                    />
                )} */}
            </div>
        </div>
    );
};

export default ParticipantExpanded;